import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, Briefcase, MapPin, DollarSign, Clock, Filter, ChevronDown, Send, Loader2 } from 'lucide-react';

// --- MOCK API DATA ---
const mockJobs = [
  { id: 201, title: 'Senior Software Engineer (Backend)', department: 'Engineering', location: 'Remote', salary: '120k - 150k', type: 'Full-time', deadline: '2025-10-30' },
  { id: 202, title: 'HR Business Partner', department: 'Human Resources', location: 'New York', salary: '90k - 110k', type: 'Full-time', deadline: '2025-11-15' },
  { id: 203, title: 'Junior Data Analyst', department: 'Data Science', location: 'San Francisco', salary: '70k - 90k', type: 'Full-time', deadline: '2025-10-25' },
  { id: 204, title: 'UX/UI Design Intern', department: 'Design', location: 'New York', salary: '50k - 60k', type: 'Contract', deadline: '2025-12-01' },
];

// --- API MOCK FUNCTION ---
const fetchJobsData = async () => new Promise(resolve => {
    setTimeout(() => resolve({ data: mockJobs }), 800);
});

// --- FRAMER MOTION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

// --- UTILITY: Job Card Component ---
const JobCard = ({ job }) => (
  <motion.div
    variants={cardVariants}
    whileHover={{ y: -5, boxShadow: '0 12px 20px rgba(79, 70, 229, 0.2)' }}
    className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-l-4 border-indigo-500 hover:border-pink-500 transition-all duration-300 flex flex-col justify-between"
  >
    <div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{job.title}</h3>
      <p className="mt-1 text-sm font-medium text-pink-600 dark:text-pink-400">{job.department}</p>

      <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 pt-3">
        <p className="flex items-center">
          <MapPin size={16} className="mr-2 text-indigo-400" /> {job.location}
        </p>
        <p className="flex items-center">
          <DollarSign size={16} className="mr-2 text-indigo-400" /> {job.salary} USD
        </p>
        <p className="flex items-center">
          <Briefcase size={16} className="mr-2 text-indigo-400" /> {job.type}
        </p>
      </div>
    </div>

    <div className="mt-5 flex items-center justify-between pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center">
        <Clock size={14} className="mr-1" /> Deadline: {job.deadline}
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => alert(`Applying for: ${job.title}`)}
        className="py-2 px-4 text-sm font-bold bg-pink-500 text-white rounded-lg shadow-md hover:bg-pink-600 transition-colors flex items-center"
      >
        <Send size={16} className="mr-1" /> Apply
      </motion.button>
    </div>
  </motion.div>
);

// --- MAIN COMPONENT: InternalJobs ---
const InternalJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');

  useEffect(() => {
    fetchJobsData().then(response => {
      setJobs(response.data);
      setLoading(false);
    });
  }, []);

  // Extract unique filters
  const departments = useMemo(() => ['All', ...new Set(jobs.map(j => j.department))].sort(), [jobs]);
  const locations = useMemo(() => ['All', ...new Set(jobs.map(j => j.location))].sort(), [jobs]);

  // Optimized filtering
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const titleMatch = job.title.toLowerCase().includes(searchTerm.toLowerCase());
      const deptMatch = selectedDept === 'All' || job.department === selectedDept;
      const locationMatch = selectedLocation === 'All' || job.location === selectedLocation;
      return titleMatch && deptMatch && locationMatch;
    });
  }, [jobs, searchTerm, selectedDept, selectedLocation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-pink-600">
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Briefcase size={48} />
        </motion.div>
        <p className="mt-4 text-xl font-medium ml-4">Searching Open Roles...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 md:p-12 min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 dark:from-gray-950 dark:to-gray-800 transition-colors duration-500">
      
      {/* Header */}
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8 md:mb-12 border-b-4 border-pink-500 pb-4"
      >
        <h1 className="text-4xl font-extrabold text-pink-700 dark:text-pink-400 tracking-tight flex items-center">
          <Briefcase size={30} className="mr-3" /> Internal Career Opportunities
        </h1>
        <p className="mt-1 text-lg text-gray-600 dark:text-gray-400">
          Grow your career with us. Explore exciting new roles within the company.
        </p>
      </motion.header>

      {/* Search and Filters Bar */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg mb-8">
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col md:flex-row gap-4"
        >
            {/* Search Input */}
            <div className="relative flex-grow">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search by job title or keyword..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full py-3 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 shadow-sm"
                />
            </div>

            {/* Department Filter */}
            <div className="relative">
                <select
                    value={selectedDept}
                    onChange={(e) => setSelectedDept(e.target.value)}
                    className="appearance-none w-full py-3 pl-4 pr-10 border border-gray-300 rounded-lg shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                >
                    <option value="All">All Departments</option>
                    {departments.filter(d => d !== 'All').map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                    ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-indigo-500" />
            </div>

            {/* Location Filter */}
            <div className="relative">
                <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="appearance-none w-full py-3 pl-4 pr-10 border border-gray-300 rounded-lg shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                >
                    <option value="All">All Locations</option>
                    {locations.filter(l => l !== 'All').map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                    ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-indigo-500" />
            </div>
        </motion.div>
      </div>

      {/* Job Listings Grid */}
      {filteredJobs.length > 0 ? (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredJobs.map(job => (
            <JobCard key={job.id} job={job} />
          ))}
        </motion.div>
      ) : (
        <motion.div 
            key="no-jobs"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-12 text-center bg-white dark:bg-gray-800 rounded-xl shadow-lg mt-10"
        >
            <Briefcase size={48} className="mx-auto text-pink-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">No Openings Found</h2>
            <p className="text-gray-500 dark:text-gray-400">Try broadening your search or filter criteria.</p>
        </motion.div>
      )}
    </div>
  );
};

export default InternalJobs;